import React, { useState, useEffect } from 'react';
import { X, Calendar, DollarSign, AlertTriangle, Info } from 'lucide-react';
import moment from 'moment';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

const CompleteReservationModal = ({ isOpen, onClose, reservation, onComplete }) => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';
    const [kmRetour, setKmRetour] = useState('');
    const [retardFee, setRetardFee] = useState(0);
    const [adjustedTotal, setAdjustedTotal] = useState(0);
    const [versement, setVersement] = useState(0);
    const [isLate, setIsLate] = useState(false);
    const [isEarly, setIsEarly] = useState(false);
    const [reste, setReste] = useState(0);
    const [resteBeforePayment, setResteBeforePayment] = useState(0);

    useEffect(() => {
        if (!reservation) return;

        const now = moment();
        const debut = moment(reservation.date_depart);
        const retourPrevu = moment(reservation.date_retour_prevue);
        const prixJour = reservation.prix_base || 0;
        const verse = reservation.versement || 0;
        setVersement(verse);

        if (now.isAfter(retourPrevu)) {
            setIsLate(true);
            setRetardFee(0); // Let user input the amount
            setAdjustedTotal(reservation.total); // Base: initial total without fees
        } else {
            const actualDays = Math.max(1, now.diff(debut, 'days'));
            const newTotal = actualDays * prixJour;
            setIsEarly(true);
            setRetardFee(0);
            setAdjustedTotal(newTotal);
        }

    }, [reservation]);

    useEffect(() => {
        const resteCalc = adjustedTotal - (versement || 0);
        setResteBeforePayment(Math.max(0, resteCalc));
        setReste(0); // Since it's always marked as paid, reste is always 0
    }, [adjustedTotal, versement]);

    const handleSubmit = () => {
        if (!kmRetour || isNaN(kmRetour)) {
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: t('valid_km_return_error'),
                confirmButtonColor: '#3b82f6'
            })
            return;
        }

        onComplete({
            id: reservation.id,
            km_retour: Number(kmRetour),
            retard_prix: retardFee,
            total: adjustedTotal,
            versement: adjustedTotal, // Always mark as fully paid
            date_retour_effective: moment().toISOString(),
            allowIncomplete: false,
            confirmLate: isLate
        });

        onClose();
    };

    if (!isOpen || !reservation) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center px-4" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative">
                <button className={`absolute top-4 ${isRtl ? 'left-4' : 'right-4'}`} onClick={onClose}>
                    <X className="w-5 h-5 text-gray-600" />
                </button>

                <h2 className={`text-xl font-bold mb-4 ${isRtl ? 'text-right' : 'text-left'}`}>{t('close_reservation_title')} #{reservation.id}</h2>

                <div className="space-y-4">
                    {/* Résumé */}
                    <div className={`text-sm text-gray-600 ${isRtl ? 'text-right' : 'text-left'}`}>
                        <div><strong>{t('departure')} :</strong> {moment(reservation.date_depart).format('DD/MM/YYYY HH:mm')}</div>
                        <div><strong>{t('return_date_time')} :</strong> {moment(reservation.date_retour_prevue).format('DD/MM/YYYY HH:mm')}</div>
                        <div><strong>{t('km_departure_label')} :</strong> {reservation.km_depart} km</div>
                        <div><strong>{t('deposit')} :</strong> {reservation.montant_daman || 0} {t('currency')}</div>
                    </div>

                    {/* Saisie KM */}
                    <div>
                        <label className={`block text-sm font-medium text-gray-700 ${isRtl ? 'text-right' : 'text-left'}`}>{t('return_mileage_label')} *</label>
                        <input
                            type="number"
                            value={kmRetour}
                            onChange={(e) => setKmRetour(e.target.value)}
                            className={`mt-1 w-full p-2 border border-gray-300 rounded ${isRtl ? 'text-right' : 'text-left'}`}
                            required
                        />
                    </div>

                    {/* Retard ou retour anticipé */}
                    {isLate && (
                        <div className={`bg-yellow-50 border-l-4 border-yellow-500 p-3 text-sm text-yellow-800 ${isRtl ? 'border-l-0 border-r-4' : ''}`}>
                            <div className={`flex items-center mb-1 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <AlertTriangle className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                <span>{t('late_return_detected')}</span>
                            </div>
                            <input
                                type="number"
                                value={retardFee}
                                onChange={(e) => {
                                    const raw = e.target.value.replace(/[,\.]/g, '');
                                    const value = Number(raw);
                                    setRetardFee(value);
                                    setAdjustedTotal(Number(reservation.total) + value);
                                }}
                                className={`mt-1 w-full p-2 border border-yellow-300 rounded ${isRtl ? 'text-right' : 'text-left'}`}
                            />
                        </div>
                    )}

                    {isEarly && (
                        <div className={`bg-blue-100 border-l-4 border-blue-500 p-3 text-sm text-blue-800 flex items-center ${isRtl ? 'border-l-0 border-r-4 flex-row-reverse' : ''}`}>
                            <Calendar className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                            <span>{t('early_return_detected')} <strong className={isRtl ? 'mr-1' : 'ml-1'}>{adjustedTotal} {t('currency')}</strong></span>
                        </div>
                    )}

                    {/* Total modifiable */}
                    <div>
                        <label className={`block text-sm font-medium text-gray-700 ${isRtl ? 'text-right' : 'text-left'}`}>{t('total_modifiable')}</label>
                        <input
                            type="number"
                            value={adjustedTotal}
                            onChange={(e) => setAdjustedTotal(Number(e.target.value))}
                            className={`mt-1 w-full p-2 border border-gray-300 rounded ${isRtl ? 'text-right' : 'text-left'}`}
                        />
                    </div>

                    {/* Paiement information */}
                    <div className={`bg-green-50 border-l-4 border-green-500 p-3 text-sm text-green-800 ${isRtl ? 'border-l-0 border-r-4' : ''}`}>
                        <div className={`flex items-center mb-1 ${isRtl ? 'flex-row-reverse' : ''}`}>
                            <Info className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                            <span>{t('reservation_fully_paid')}</span>
                        </div>
                    </div>

                    {/* Reste à payer before payment */}
                    {reservation.mode_paiement === 'versement' && (
                        <div className={`text-sm text-gray-700 ${isRtl ? 'text-right' : 'text-left'}`}>
                            {t('remaining_before_closing')} : <strong>{resteBeforePayment} {t('currency')}</strong>
                        </div>
                    )}


                </div>

                <div className={`mt-6 flex justify-end ${isRtl ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                    <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded">
                        {t('cancel')}
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                        {t('close_reservation_btn')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CompleteReservationModal;
